import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Text,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Button,
	HStack,
	Icon,
	Center,
	Spinner,
	Divider,
} from "@chakra-ui/react";
import { FaCheck, FaArrowLeft, FaUndo, FaIdCard } from "react-icons/fa";
import ListAnalysts from "components/analyst/ListAnalysts";
import AnalystConfigComponent from "components/analyst/AnalystConfigComponent";
import AnalystDataAccessComponent from "components/analyst/AnalystDataAccessComponent";
import { AnalystProto, ScenarioAnalysis } from "build-ts/analyst";
import AnalystScenariosComponent from "components/analyst/AnalystScenariosComponent";
import AnalystJobsComponent from "components/analyst/AnalystJobsComponent";
import { Header } from "build-ts/metadata";
import { getAnalyst, createAnalyst, updateAnalyst, refreshAnalystDataScope } from "services/AnalystService";
import { listScheduledJobs } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { MetadataTypeEnum } from "build-ts/enums";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import lodash from "lodash";
import { FiDatabase } from "react-icons/fi";
import { useAnalystChat } from "context/AnalystChatContext";
import { BsChatDots, BsRobot } from "react-icons/bs";
import { LuClipboardList } from "react-icons/lu";
import { MdSchedule } from "react-icons/md";

const AnalystPage: React.FC = () => {
	const [selectedAnalystData, setSelectedAnalystData] = useState<AnalystProto | undefined>(undefined);
	// store original analyst data for discarded changes
	const [originalAnalystData, setOriginalAnalystData] = useState<AnalystProto | undefined>(undefined);
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
	const [scenarios, setScenarios] = useState<ScenarioAnalysis[]>([]);
	const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
	const [isLoadingAnalystDetails, setIsLoadingAnalystDetails] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isTabActive, setIsTabActive] = useState<number>(0);
	const [hasChanges, setHasChanges] = useState<boolean>(false);
	const [hasDiscardedChanges, setHasDiscardedChanges] = useState<boolean>(false);
	// States for scenario creation/editing - used to control overlay button visibility
	const [isCreatingScenario, setIsCreatingScenario] = useState<boolean>(false);
	const [isEditingScenario, setIsEditingScenario] = useState<boolean>(false);
	// State for job viewing/editing - used to control overlay button visibility
	const [isViewingOrEditingJob, setIsViewingOrEditingJob] = useState<boolean>(false);
	// When true, user is on ChatPage in scenario creation mode (from Analyses tab) — hide top bar and overlay
	const [isOnScenarioCreationChat, setIsOnScenarioCreationChat] = useState<boolean>(false);
	// state to track data scope changes
	const [hasDataScopeChanges, setHasDataScopeChanges] = useState<boolean>(false);
	// State for analyst jobs map
	const [analystJobsMap, setAnalystJobsMap] = useState<Map<string, Header[]>>(new Map());
	const [activeAnalystsMap, setActiveAnalystsMap] = useState<{
		analystId: string,
		active: boolean,
		noOfActiveJobs: number,
		noOfTotalJobs: number
	}[]>([]);
	const [isLoadingAllJobs, setIsLoadingAllJobs] = useState<boolean>(false);

	const { setAnalystsList, analystsList, isNavbarCollapsed, isLoadingAnalysts } = useAnalystChat();

	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();

	// Use refs to store original individual pieces for change detection
	const originalDataRef = useRef<{
		name: string;
		description: string;
		role: string;
		instructions: string;
		notes: string;
		// Track data scope
		dsInstructions: string;
		dsUseDefaultScope: boolean;
		dsMetrics: string[];
		dsBenchmarks: string[];
		dsColumnScopes: Array<{
			id: string | undefined;
			operator: number | undefined;
			values: string[] | undefined;
			is_hidden: boolean | undefined;
		}>;
		scenarios: ScenarioAnalysis[];
	}>({
		name: "",
		description: "",
		role: "",
		instructions: "",
		notes: "",
		dsInstructions: "",
		dsUseDefaultScope: true,
		dsMetrics: [],
		dsBenchmarks: [],
		dsColumnScopes: [],
		scenarios: [],
	});

	// Detect changes by comparing current individual pieces with original
	useEffect(() => {
		if (isCreatingNew) {
			// For new analysts, no change detection needed
			setHasChanges(false);
			return;
		}

		if (!selectedAnalystData || isLoadingAnalystDetails) {
			setHasChanges(false);
			return;
		}

		// Extract current dataScope
		const currentDsInstructions = selectedAnalystData.data_scope?.instructions || "";
		const currentDsUseDefaultScope = selectedAnalystData.data_scope?.use_default_scope || false;
		const currentDsMetrics = (selectedAnalystData.data_scope?.metrics || [])
			.map(m => m.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsBenchmarks = (selectedAnalystData.data_scope?.benchmarks || [])
			.map(b => b.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsColumnScopes = (selectedAnalystData.data_scope?.column_scopes || [])
			.map(cs => ({
				id: cs?.kg_column?.header?.id,
				operator: cs.operator,
				values: cs.values ? [...cs.values].sort() : undefined,
				is_hidden: cs.is_hidden,
			}));

		// Compare each field - use lodash.isEqual for arrays
		const hasChanged =
			(selectedAnalystData.header?.name || "") !== originalDataRef.current.name ||
			(selectedAnalystData.header?.description || "") !== originalDataRef.current.description ||
			(selectedAnalystData.role || "") !== originalDataRef.current.role ||
			(selectedAnalystData.instructions || "") !== originalDataRef.current.instructions ||
			(selectedAnalystData.notes || "") !== originalDataRef.current.notes ||
			!lodash.isEqual(scenarios, originalDataRef.current.scenarios);

		// Check if data scope has changed
		const dataScopeChanged =
			currentDsInstructions !== originalDataRef.current.dsInstructions ||
			currentDsUseDefaultScope !== originalDataRef.current.dsUseDefaultScope ||
			!lodash.isEqual(currentDsMetrics, originalDataRef.current.dsMetrics) ||
			!lodash.isEqual(currentDsBenchmarks, originalDataRef.current.dsBenchmarks) ||
			!lodash.isEqual(currentDsColumnScopes, originalDataRef.current.dsColumnScopes);

		setHasDataScopeChanges(dataScopeChanged);

		setHasChanges(hasChanged || dataScopeChanged);

	}, [selectedAnalystData, scenarios, isCreatingNew, isLoadingAnalystDetails]);

	// Fetch jobs for all analysts when analysts are loaded
	useEffect(() => {
		const fetchAllAnalystJobs = async () => {
			if (!isLoadingAnalysts && analystsList.length > 0) {
				setIsLoadingAllJobs(true);
				setActiveAnalystsMap([]); // Reset before fetching
				try {
					// Fetch jobs for all analysts in parallel
					const jobPromises = analystsList.map(async (analyst) => {
						if (analyst.id) {
							const response = await listScheduledJobs(
								"ANALYST_JOB",
								-1,
								1,
								analyst.id,
								"LAST_MODIFIED",
								false,
								true, /* includeScheduleActivity */
								"", /* activeSchedule */
							);
							return { analystId: analyst.id, jobs: response || [] };
						}
						return { analystId: analyst.id || "", jobs: [] };
					});

					const results = await Promise.all(jobPromises);

					// Build the map
					const newMap = new Map<string, Header[]>();
					results.forEach(({ analystId, jobs }) => {
						if (analystId) {
							newMap.set(analystId, jobs);
						}
					});

					setAnalystJobsMap(newMap);

					// Build active analysts map
					const activeAnalysts: {
						analystId: string;
						active: boolean;
						noOfActiveJobs: number;
						noOfTotalJobs: number;
					}[] = [];

					newMap.forEach((jobs, analystId) => {
						const activeJobs = jobs.filter(job => job.has_active_schedule);
						const totalJobs = jobs.length;
						activeAnalysts.push({
							analystId,
							active: activeJobs.length > 0,
							noOfActiveJobs: activeJobs.length,
							noOfTotalJobs: totalJobs
						});
					});

					setActiveAnalystsMap(activeAnalysts);
				} catch (error) {
					console.error("Error fetching all analyst jobs:", error);
				} finally {
					setIsLoadingAllJobs(false);
				}
			}
		};

		fetchAllAnalystJobs();
	}, [isLoadingAnalysts, analystsList]);

	// Handle analyst loading from URL parameter
	useEffect(() => {
		const loadAnalyst = async () => {
			if (id && analystsList.length > 0 && !selectedAnalyst) {
				// Handle loading existing analyst from URL
				const analyst = analystsList.find(analyst => analyst.id === id);
				if (analyst) {
					setIsLoadingAnalystDetails(true);
					setSelectedAnalyst(analyst);
					setIsCreatingNew(false);
					try {
						if (analyst?.id) {
							const analystData = await getAnalyst(analyst.id);
							setSelectedAnalystData(analystData || undefined);
							setOriginalAnalystData(analystData || undefined);
							setScenarios(analystData?.scenarios || []);

							// Store original data as individual pieces (arrays, not JSON strings)
							originalDataRef.current = {
								name: analystData?.header?.name || "",
								description: analystData?.header?.description || "",
								role: analystData?.role || "",
								instructions: analystData?.instructions || "",
								notes: analystData?.notes || "",
								dsInstructions: analystData?.data_scope?.instructions || "",
								dsUseDefaultScope: analystData?.data_scope?.use_default_scope || false,
								dsMetrics: (analystData?.data_scope?.metrics || [])
									.map(m => m.header?.id)
									.filter((id): id is string => !!id)
									.sort(),
								dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
									.map(b => b.header?.id)
									.filter((id): id is string => !!id)
									.sort(),
								dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
									.map(cs => ({
										id: cs?.kg_column?.header?.id,
										operator: cs.operator,
										values: cs.values ? [...cs.values].sort() : undefined,
										is_hidden: cs.is_hidden,
									})),
								scenarios: analystData?.scenarios || [],
							};
						}
					} catch (error) {
						console.error("Error fetching analyst details:", error);
						setSelectedAnalystData(undefined);
						setOriginalAnalystData(undefined);
					} finally {
						setIsLoadingAnalystDetails(false);
					}
				}
			}
		};
		loadAnalyst();
	}, [id, analystsList, selectedAnalyst]);

	// Handle tab switching based on URL
	useEffect(() => {
		if (location.pathname.includes("/data-access")) {
			setIsTabActive(1);
		} else if (location.pathname.includes("/scenarios")) {
			setIsTabActive(2);
		} else if (location.pathname.includes("/jobs")) {
			setIsTabActive(3);
		} else {
			setIsTabActive(0);
		}
	}, [location.pathname]);

	// Handle analyst selection for editing
	const handleAnalystSelection = async (analyst: Header) => {
		setIsLoadingAnalystDetails(true);
		setSelectedAnalyst(analyst);
		setIsCreatingNew(false);
		// Update URL to include analyst ID
		if (analyst?.id) {
			navigate(`/analyst/${analyst.id}`, { replace: true });
		}

		// Switch to Config tab if currently on other tabs
		if (isTabActive !== 0) {
			setIsTabActive(0);
		}

		// Fetch analyst data
		try {
			if (analyst?.id) {
				const analystData = await getAnalyst(analyst.id);
				setSelectedAnalystData(analystData || undefined);
				setOriginalAnalystData(analystData || undefined);
				setScenarios(analystData?.scenarios || []);

				// Store original data as individual pieces (arrays, not JSON strings)
				originalDataRef.current = {
					name: analystData?.header?.name || "",
					description: analystData?.header?.description || "",
					role: analystData?.role || "",
					instructions: analystData?.instructions || "",
					notes: analystData?.notes || "",
					dsInstructions: analystData?.data_scope?.instructions || "",
					dsUseDefaultScope: analystData?.data_scope?.use_default_scope || false,
					dsMetrics: (analystData?.data_scope?.metrics || [])
						.map(m => m.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
						.map(b => b.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
						.map(cs => ({
							id: cs?.kg_column?.header?.id,
							operator: cs.operator,
							values: cs.values ? [...cs.values].sort() : undefined,
							is_hidden: cs.is_hidden,
						})),
					scenarios: analystData?.scenarios || [],
				};
			}
		} catch (error) {
			console.error("Error fetching analyst details:", error);
			setSelectedAnalystData(undefined);
		} finally {
			setIsLoadingAnalystDetails(false);
		}
	};

	// Handle creating a new analyst
	const handleCreateNewAnalyst = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setScenarios([]);
		setIsCreatingNew(true);
		setIsTabActive(0); // Ensure we're on Config tab
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			description: "",
			role: "",
			instructions: "",
			notes: "",
			dsInstructions: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
			scenarios: [],
		};

		navigate("/analyst");
	};

	// Handle back to list
	const handleBackToList = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setScenarios([]);
		setIsCreatingNew(false);
		setIsTabActive(0);
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			description: "",
			role: "",
			instructions: "",
			notes: "",
			dsInstructions: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
			scenarios: [],
		};

		navigate("/analyst");
	};

	// Handle save analyst (create new or update existing)
	// Optional updatedScenarios param is used when called from scenario save (since state update is async)
	const handleSaveAnalyst = async (updatedScenarios?: ScenarioAnalysis[]) => {
		if (!selectedAnalystData) {
			showToast("No analyst data to save", "", "warning");
			return;
		}

		// Validate required fields
		if (!selectedAnalystData.header?.name || selectedAnalystData.header.name.trim() === "") {
			showToast("Please enter analyst name", "", "warning");
			return;
		}

		setIsSaving(true);
		try {
			if (isCreatingNew) {
				// Create new analyst
				const newAnalyst: AnalystProto = {
					...selectedAnalystData,
					header: {
						...selectedAnalystData.header,
						id: uuidv4(),
						type: MetadataTypeEnum.ANALYST,
						created_date_ms: Date.now(),
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
					},
					scenarios: [], // Initially empty
				};

				const result = await createAnalyst(newAnalyst);

				if (result && result.header) {
					showToast("Analyst created successfully", "", "success");

					// Add new analyst to top of list
					setAnalystsList(prev => [result.header!, ...prev]);

					// Select the newly created analyst
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);
					setIsCreatingNew(false);

					// Store as new original data (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						description: result.header?.description || "",
						role: result.role || "",
						instructions: result.instructions || "",
						notes: result.notes || "",
						dsInstructions: result.data_scope?.instructions || "",
						dsUseDefaultScope: result.data_scope?.use_default_scope || false,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
							})),
						scenarios: result.scenarios || [],
					};
					setHasChanges(false);

					// Navigate to the saved analyst's URL
					if (result.header?.id) {
						navigate(`/analyst/${result.header.id}`, { replace: true });
					}
				}
			} else {
				// Update existing analyst
				const updatedAnalyst: AnalystProto = {
					...selectedAnalystData,
					header: {
						...selectedAnalystData.header,
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
					},
					scenarios: updatedScenarios ?? scenarios,
				};

				const result = await updateAnalyst(updatedAnalyst, hasDataScopeChanges);

				if (result && result.header) {
					showToast("Analyst updated successfully", "", "success");

					// Update the analyst in the list
					setAnalystsList(prev =>
						prev.map(h => h.id === result.header!.id ? result.header! : h)
					);

					// Update local state
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);
					setScenarios(result.scenarios || []);

					// Update original data to new saved state (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						description: result.header?.description || "",
						role: result.role || "",
						instructions: result.instructions || "",
						notes: result.notes || "",
						dsInstructions: result.data_scope?.instructions || "",
						dsUseDefaultScope: result.data_scope?.use_default_scope || false,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
							})),
						scenarios: result.scenarios || [],
					};
					setHasChanges(false);

					if (hasDataScopeChanges && result.header?.id) {
						refreshAnalystDataScope(result.header.id, true);
					}
				}
			}
		} catch (error) {
			showToast("Failed to save analyst", "", "error");
			console.error("Error saving analyst:", error);
		} finally {
			setIsSaving(false);
		}
	};

	// Handle cancel or discard changes - reset to original data
	const handleCancel = () => {
		if (hasChanges && !isCreatingNew && selectedAnalystData) {
			if (originalAnalystData) {
				// Reset selectedAnalystData to original
				setSelectedAnalystData({ ...originalAnalystData });
				setScenarios(originalAnalystData.scenarios || []);
				setHasDiscardedChanges(true);
				showToast("Changes discarded", "", "info");
			} else {
				handleBackToList();
			}
		} else if (isCreatingNew) {
			// Go back to list for new analyst
			handleBackToList();
		}
	};

	// Handle tab change
	const handleTabChange = (index: number) => {
		setIsTabActive(index);
		if (index === 0) {
			// Navigate to config tab
			if (isCreatingNew) {
				// Need to do this because when we navigate back during creation,
				// then /analyst/undefined is shown which is not a valid URL
				navigate("/analyst");
			} else {
				navigate(`/analyst/${id}`);
			}
		} else if (index === 1) {
			// Navigate to data access tab

			if (isCreatingNew) {
				// Need to do this because when we navigate back during creation,
				// then /analyst/undefined/data-access is shown which is not a valid URL
				navigate("/analyst");
			} else {
				navigate(`/analyst/${id}/data-access`);
			}
		} else if (index === 2) {
			// Navigate to scenarios tab
			navigate(`/analyst/${id}/scenarios`);
		} else if (index === 3) {
			// Navigate to jobs tab
			navigate(`/analyst/${id}/jobs`);
		}
	};

	// Handle opening chat in new tab
	const handleOpenChat = () => {
		if (selectedAnalyst?.id) {
			window.open(`/chat?chatAnalystId=${selectedAnalyst.id}`, "_blank");
		}
	};

	const renderTabs = () => (
		<Box height="100%" bg="white">
			<Tabs
				colorScheme="purple"
				variant="unstyled"
				size="md"
				height="100%"
				defaultIndex={0}
				index={isTabActive}
				onChange={handleTabChange}
			>
				{/* Header with Back Button and Pill Tabs - hidden when on Scenario Creation Chat */}
				{!isOnScenarioCreationChat && (
				<HStack
					p={3}
					borderBottom="1px"
					borderColor="gray.200"
					justify="space-between"
				>
					{/* Left Side: Back Button + Analyst Name */}
					<HStack spacing={4}>
						{/* Back Button */}
						<Button
							leftIcon={<Icon as={FaArrowLeft} />}
							variant="ghost"
							size="sm"
							onClick={handleBackToList}
							color="gray.700"
							_hover={{ bg: "gray.100" }}
							flexShrink={0}
							iconSpacing={0}
						/>

						{/* Analyst Name */}
						<HStack spacing={3}>
							<Icon as={BsRobot} boxSize={7} color="purple.700" />
							<Text
								fontSize="md"
								fontWeight="600"
								color="gray.800"
								mb={0}
								align="left"
								noOfLines={1}
							>
								{selectedAnalystData?.header?.name || (isCreatingNew ? "Creating New Analyst" : "Untitled Analyst")}
							</Text>
						</HStack>
					</HStack>

					{/* Right Side: Pill-style Tabs */}
					<TabList gap={3} justifyContent="flex-end">
						<Tab
							bg="gray.100"
							borderRadius="xl"
							px={3}
							py={2}
							fontSize="md"
							fontWeight="600"
							color="gray.600"
							_selected={{
								bg: "purple.600",
								color: "white",
								boxShadow: "sm",
								_hover: {
									bg: "purple.700",  // Darker purple when hovering over selected tab
								}
							}}
							_hover={{
								bg: selectedAnalyst ? "gray.200" : "purple.600",
								transform: selectedAnalyst ? "translateY(-1px)" : "none",
							}}
							transition="all 0.2s"
						>
							<HStack spacing={2}>
								<Icon as={FaIdCard} boxSize={5} />
								<Text mb={0} fontSize="md" fontWeight="700" >Profile</Text>
							</HStack>
						</Tab>
						<Divider
							orientation="vertical"
							height="auto"
							alignSelf="stretch"
							borderColor="purple.700"
							mx={0}
							my={2}
						/>
						<Tab
							bg="gray.100"
							borderRadius="xl"
							px={3}
							py={2}
							fontSize="md"
							fontWeight="600"
							color="gray.600"
							_selected={{
								bg: "purple.600",
								color: "white",
								boxShadow: "sm",
								_hover: {
									bg: "purple.700",  // Darker purple when hovering over selected tab
								}
							}}
							_hover={{
								bg: selectedAnalyst ? "gray.200" : "purple.600",
								transform: selectedAnalyst ? "translateY(-1px)" : "none",
							}}
							transition="all 0.2s"
						>
							<HStack spacing={2} whiteSpace="nowrap">
								<Icon as={FiDatabase} boxSize={5} />
								<Text mb={0} fontSize="md" fontWeight="700" whiteSpace="nowrap">Data Scope</Text>
							</HStack>
						</Tab>
						{/* Analyses and Jobs tabs - Only show when not creating new analyst */}
						{!isCreatingNew && (
							<>
								<Divider
									orientation="vertical"
									height="auto"
									alignSelf="stretch"
									borderColor="purple.700"
									mx={0}
									my={2}
								/>
								<Tab
									isDisabled={!selectedAnalyst || !selectedAnalyst.id}
									bg="gray.100"
									borderRadius="xl"
									px={3}
									py={2}
									fontSize="md"
									fontWeight="600"
									color="gray.600"
									_selected={{
										bg: "purple.600",
										color: "white",
										boxShadow: "sm",
										_hover: {
											bg: "purple.700",  // Darker purple when hovering over selected tab
										}
									}}
									_hover={{
										bg: selectedAnalyst ? "gray.200" : "purple.600",
										transform: selectedAnalyst ? "translateY(-1px)" : "none",
									}}
									transition="all 0.2s"
								>
									<HStack spacing={2}>
										<Icon as={LuClipboardList} boxSize={6} />
										<Text mb={0} fontSize="md" fontWeight="700">Analyses</Text>
									</HStack>
								</Tab>
								<Divider
									orientation="vertical"
									height="auto"
									alignSelf="stretch"
									borderColor="purple.700"
									mx={0}
									my={2}
								/>
								<Tab
									isDisabled={!selectedAnalyst || !selectedAnalyst.id}
									bg="gray.100"
									borderRadius="xl"
									px={3}
									py={2}
									fontSize="md"
									fontWeight="600"
									color="gray.600"
									_selected={{
										bg: "purple.600",
										color: "white",
										boxShadow: "sm",
										_hover: {
											bg: "purple.700",  // Darker purple when hovering over selected tab
										}
									}}
									_hover={{
										bg: selectedAnalyst ? "gray.200" : "purple.600",
										transform: selectedAnalyst ? "translateY(-1px)" : "none",
									}}
									transition="all 0.2s"
								>
									<HStack spacing={2}>
										<Icon as={MdSchedule} boxSize={6} />
										<Text mb={0} fontSize="md" fontWeight="700">Schedules</Text>
									</HStack>
								</Tab>
							</>
						)}
					</TabList>
				</HStack>
				)}

				<TabPanels height={isOnScenarioCreationChat ? "100%" : "calc(100% - 73px)"} bg="white">
					<TabPanel height="100%" p={0}>
						{/* Config Content */}
						<Box p={0} overflowY="auto" h="100%">
							{isLoadingAnalystDetails ? (
								<Center height="100%">
									<Spinner size="lg" />
									<Text ml={4}>Loading analyst details...</Text>
								</Center>
							) : (
								<AnalystConfigComponent
									analyst={selectedAnalystData}
									setAnalyst={setSelectedAnalystData}
									isCreatingNew={isCreatingNew}
									hasDiscardedChanges={hasDiscardedChanges}
									setHasDiscardedChanges={setHasDiscardedChanges}
									originalAnalyst={originalAnalystData}
									onNavigateToDataAccess={() => setIsTabActive(1)}
								/>
							)}
						</Box>
					</TabPanel>
					{/* Data Access Tab */}
					<TabPanel height="100%" p={0}>
						{/* Data Access Content */}
						<Box height="100%">
							{isLoadingAnalystDetails ? (
								<Center height="100%">
									<Spinner size="lg" />
									<Text ml={4}>Loading data access settings...</Text>
								</Center>
							) : (
								<AnalystDataAccessComponent
									analyst={selectedAnalystData}
									setAnalyst={setSelectedAnalystData}
									isCreatingNew={isCreatingNew}
									hasDiscardedChanges={hasDiscardedChanges}
									setHasDiscardedChanges={setHasDiscardedChanges}
									originalAnalyst={originalAnalystData}
								/>
							)}
						</Box>
					</TabPanel>
					<TabPanel height="100%" p={0}>
						{/* Scenarios Content */}
						<Box height="100%">
							<AnalystScenariosComponent
								dataScope={selectedAnalystData?.data_scope}
								scenarios={scenarios}
								setScenarios={setScenarios}
								isTabActive={isTabActive}
								isCreatingScenario={isCreatingScenario}
								setIsCreatingScenario={setIsCreatingScenario}
								isEditingScenario={isEditingScenario}
								setIsEditingScenario={setIsEditingScenario}
								isNavbarCollapsed={isNavbarCollapsed}
								onSaveAnalyst={handleSaveAnalyst}
								analystId={selectedAnalyst?.id}
								onScenarioChatOpenChange={setIsOnScenarioCreationChat}
							/>
						</Box>
					</TabPanel>
					<TabPanel height="100%" p={0}>
						<AnalystJobsComponent
							analystId={selectedAnalyst?.id}
							scenarios={scenarios}
							isTabActive={isTabActive}
							setIsViewingOrEditingJob={setIsViewingOrEditingJob}
							analystJobsMap={analystJobsMap}
							setAnalystJobsMap={setAnalystJobsMap}
						/>
					</TabPanel>
				</TabPanels>
			</Tabs>

			{/* Fixed Overlay Buttons - Show only when creating/editing analyst AND NOT creating/editing scenario/job AND NOT on scenario creation chat */}
			{(isCreatingNew || hasChanges || selectedAnalyst) && !isCreatingScenario && !isEditingScenario && !isViewingOrEditingJob && !isOnScenarioCreationChat && (
				<Box
					position="fixed"
					bottom="-5px"
					left={isNavbarCollapsed ? "calc(50vw + 30px)" : "calc(50vw + 120px)"}
					transform="translateX(-50%)"
					bg="white"
					border="2px"
					borderColor="gray.300"
					borderRadius="lg"
					p={2}
					zIndex={1000}
				>
					<HStack spacing={3} justify="space-around">
						{/* Chat Button - Only show when not creating new */}
						{selectedAnalyst && !isCreatingNew && (
							<Button
								leftIcon={<Icon as={BsChatDots} boxSize={5} />}
								onClick={handleOpenChat}
								size="lg"
								colorScheme="purple"
								bg="purple.700"
								_hover={{ bg: "purple.600" }}
								color="white"
								fontSize="lg"
								fontWeight="600"
							>
								Chat
							</Button>
						)}

						{/* Save/Update Button - Only show when creating or has changes, but hide on Config tab (0) when creating new */}
						{(isCreatingNew || hasChanges) && !(isCreatingNew && isTabActive === 0) && (
							<Button
								colorScheme="green"
								size="lg"
								onClick={() => handleSaveAnalyst()}
								isLoading={isSaving}
								loadingText={isCreatingNew ? "Saving..." : "Updating..."}
								leftIcon={<Icon as={FaCheck} boxSize={5} />}
								fontSize="lg"
								fontWeight="600"
								transition="all 0.2s"
							>
								{isCreatingNew ? "Save" : "Update"}
							</Button>
						)}

						{/* Cancel/Discard Button - Only show when creating or has changes */}
						{(isCreatingNew || hasChanges) && (
							<Button
								colorScheme="red"
								variant="outline"
								size="lg"
								onClick={handleCancel}
								isDisabled={isSaving}
								leftIcon={<Icon as={FaUndo} boxSize={5} />}
								fontSize="lg"
								fontWeight="600"
							>
								{isCreatingNew ? "Cancel" : "Discard"}
							</Button>
						)}
					</HStack>
				</Box>
			)}
		</Box>
	);

	const renderMainContent = () => {
		if (selectedAnalystData || isCreatingNew) {
			return renderTabs();
		}

		return null; // Grid view will be shown instead
	};

	return (
		<Box width="100%" height="100vh" bg="gray.50">
			{selectedAnalystData || isCreatingNew ? (
				// Show main content when analyst is selected or creating new
				<Box width="100%" height="100%">
					{renderMainContent()}
				</Box>
			) : (
				// Show grid view when no analyst is selected
				<Box width="100%" height="100%">
					<ListAnalysts
						analystHeaders={analystsList}
						setAnalystHeaders={setAnalystsList}
						selectedAnalyst={selectedAnalyst}
						onAnalystSelection={handleAnalystSelection}
						onCreateNewAnalyst={handleCreateNewAnalyst}
						onBackToList={handleBackToList}
						isCreatingNew={isCreatingNew}
						setIsCreatingNew={setIsCreatingNew}
						isLoading={isLoadingAnalysts}
						isLoadingAllJobs={isLoadingAllJobs}
						activeAnalystsMap={activeAnalystsMap}
					/>
				</Box>
			)}
		</Box>
	);
};

export default AnalystPage;
